import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Star, Truck, RotateCcw, CreditCard, ChevronRight, HeadphonesIcon, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { getProduct, getReviews } from '../lib/api';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

// Real product images
const PRODUCT_IMAGES = {
  glove: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/1ezyvaud_IMG_9627.png',
  packaging: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/mrpk2r4p_GHRB.png',
  useCases: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/io9eecdv_C3B90749-3B62-449A-ABCC-0F3E5030098E.png',
  pets: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/7usx8lmo_E84D7770-ABC8-4B32-91DD-87363C019FAB.png',
  storytelling: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/9gnja629_7E6C3F5B-B34B-4EAC-BEA7-5A63035C229E.png',
};

const ProductPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [stockCount] = useState(Math.floor(Math.random() * 12) + 8);
  const { addToCart } = useCart();

  // Use real images for Gloovy product
  const productImages = slug === 'gloovy' ? [
    PRODUCT_IMAGES.packaging,
    PRODUCT_IMAGES.glove,
    PRODUCT_IMAGES.storytelling,
    PRODUCT_IMAGES.useCases,
    PRODUCT_IMAGES.pets,
  ] : null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productData, reviewsData] = await Promise.all([
          getProduct(slug),
          getReviews()
        ]);
        setProduct(productData);
        const safeReviews = Array.isArray(reviewsData) ? reviewsData : [];
        setReviews(safeReviews.filter(r => r.product_id === productData.id));
        if (productData.bundles?.length > 0) {
          const popularBundle = productData.bundles.find(b => b.popular) || productData.bundles[1];
          setSelectedBundle(popularBundle);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, selectedBundle);
      toast.success('Ajouté au panier !', {
        description: selectedBundle ? selectedBundle.name : product.name,
        action: {
          label: 'Voir le panier',
          onClick: () => window.location.href = '/panier'
        }
      });
    }
  };

  const averageRating = reviews?.length > 0 
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length 
    : 4.9;

  const images = productImages || product?.images || [];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#1a1a1a] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="font-outfit text-2xl font-bold mb-4">Produit non trouvé</h1>
          <Link to="/produits">
            <Button className="bg-[#1a1a1a] hover:bg-black text-white rounded-full">
              Retour aux produits
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="product-page" className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-[#FAFAFA] py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm font-jakarta">
            <Link to="/" className="text-gray-500 hover:text-[#1a1a1a]">Accueil</Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <Link to="/produits" className="text-gray-500 hover:text-[#1a1a1a]">Produits</Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-[#1a1a1a] font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Section */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            
            {/* Images - Clean */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="space-y-4"
            >
              <div className="aspect-square rounded-2xl overflow-hidden bg-[#FAFAFA]">
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-contain p-4"
                  data-testid="product-main-image"
                />
              </div>
              
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-[#1a1a1a]' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                      data-testid={`product-thumbnail-${index}`}
                    >
                      <img src={image} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Details - Clean */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              {/* Urgency - Simple */}
              <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-2 mb-4 inline-flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                <span className="font-jakarta text-sm text-red-600 font-medium">
                  Plus que {stockCount} en stock
                </span>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-5 h-5 ${i < Math.round(averageRating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}
                    />
                  ))}
                </div>
                <span className="font-jakarta font-medium text-[#1a1a1a]">{averageRating.toFixed(1)}/5</span>
                <span className="font-jakarta text-gray-500 text-sm">({(reviews?.length || 0) + 847} avis)</span>
              </div>

              {/* Title - Simple */}
              <h1 
                className="font-outfit text-3xl lg:text-4xl font-bold text-[#1a1a1a] mb-4"
                data-testid="product-title"
              >
                Gloovy – Le gant anti-poils
              </h1>

              <p className="font-jakarta text-gray-600 mb-6 leading-relaxed">
                Élimine instantanément les poils d'animaux de toutes les surfaces. Un seul passage suffit.
              </p>

              {/* Features - Simple Checklist */}
              <div className="bg-[#FAFAFA] rounded-xl p-5 mb-6">
                <ul className="space-y-3">
                  {product.features?.slice(0, 4).map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="font-jakarta text-[#1a1a1a] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Bundle Selection - Clean */}
              {product.bundles?.length > 0 && (
                <div className="mb-6">
                  <p className="font-jakarta font-semibold text-[#1a1a1a] mb-3">
                    Choisissez votre offre :
                  </p>
                  <div className="space-y-2">
                    {product.bundles.map((bundle) => (
                      <button
                        key={bundle.id}
                        onClick={() => setSelectedBundle(bundle)}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                          selectedBundle?.id === bundle.id 
                            ? 'border-[#1a1a1a] bg-gray-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        } ${bundle.popular ? 'relative' : ''}`}
                        data-testid={`bundle-option-${bundle.id}`}
                      >
                        {bundle.popular && (
                          <span className="absolute -top-2 left-4 bg-green-500 text-white font-jakarta font-medium text-xs px-2 py-0.5 rounded-full">
                            Le plus populaire
                          </span>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              selectedBundle?.id === bundle.id ? 'border-[#1a1a1a] bg-[#1a1a1a]' : 'border-gray-300'
                            }`}>
                              {selectedBundle?.id === bundle.id && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div>
                              <span className="font-jakarta font-medium text-[#1a1a1a]">{bundle.name}</span>
                              <span className="block font-jakarta text-sm text-green-600">-{bundle.savings}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-outfit text-xl font-bold text-[#1a1a1a]">{bundle.price.toFixed(2)}€</p>
                            {bundle.original_price && (
                              <p className="font-jakarta text-sm text-gray-400 line-through">{bundle.original_price.toFixed(2)}€</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Add to Cart - Clean */}
              <Button 
                size="lg"
                onClick={handleAddToCart}
                className="w-full bg-[#1a1a1a] hover:bg-black text-white font-jakarta font-semibold py-6 rounded-full text-base"
                data-testid="add-to-cart-btn"
              >
                Ajouter au panier – {selectedBundle ? selectedBundle.price.toFixed(2) : product.price?.toFixed(2)}€
              </Button>

              {/* Trust */}
              <div className="flex flex-wrap justify-center gap-4 mt-4 text-gray-500 font-jakarta text-sm">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Livraison gratuite
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Retour 30 jours
                </span>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-4 gap-2 mt-6 pt-6 border-t border-gray-100">
                {[
                  { icon: Truck, text: 'Livraison 48h' },
                  { icon: RotateCcw, text: 'Retour 30j' },
                  { icon: CreditCard, text: 'Paiement sécurisé' },
                  { icon: HeadphonesIcon, text: 'Support 24h' }
                ].map((badge, index) => (
                  <div key={index} className="text-center">
                    <badge.icon className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <span className="font-jakarta text-xs text-gray-500 block">{badge.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Storytelling Image */}
      {slug === 'gloovy' && (
        <section className="bg-white">
          <img 
            src={PRODUCT_IMAGES.storytelling}
            alt="Avant/Après Gloovy"
            className="w-full h-auto"
          />
        </section>
      )}

      {/* FAQ */}
      {product.faq?.length > 0 && (
        <section className="py-12 lg:py-16 bg-[#FAFAFA]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-outfit text-2xl font-bold text-[#1a1a1a] mb-8 text-center">
              Questions fréquentes
            </h2>
            <Accordion type="single" collapsible className="space-y-2">
              {product.faq.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="border border-gray-200 rounded-xl px-5 bg-white"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="font-jakarta font-medium text-[#1a1a1a] hover:no-underline py-4 text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-jakarta text-gray-600 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Reviews */}
      {reviews?.length > 0 && (
        <section className="py-12 lg:py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-outfit text-2xl font-bold text-[#1a1a1a] mb-1">Avis clients</h2>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <span className="font-jakarta text-sm text-gray-600">{averageRating.toFixed(1)}/5 • {(reviews?.length || 0) + 847} avis</span>
                </div>
              </div>
              <Link to="/avis">
                <Button variant="outline" className="border-gray-300 text-[#1a1a1a] hover:bg-gray-50 rounded-full text-sm">
                  Voir tous
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {reviews.slice(0, 3).map((review, index) => (
                <div 
                  key={review.id}
                  className="bg-[#FAFAFA] p-5 rounded-xl"
                  data-testid={`product-review-${index}`}
                >
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                    ))}
                  </div>
                  <p className="font-jakarta text-gray-700 text-sm mb-3">"{review.comment}"</p>
                  <div className="flex items-center justify-between">
                    <span className="font-jakarta font-medium text-[#1a1a1a] text-sm">{review.author_name}</span>
                    {review.is_verified && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Vérifié
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-12 lg:py-16 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-outfit text-2xl lg:text-3xl font-bold text-white mb-4">
            Satisfait ou remboursé
          </h3>
          <p className="font-jakarta text-white/70 mb-6">
            30 jours pour tester. Sans risque.
          </p>
          <Button 
            size="lg"
            onClick={handleAddToCart}
            className="bg-white text-[#1a1a1a] hover:bg-gray-100 font-jakarta font-semibold px-8 py-6 rounded-full"
            data-testid="final-add-to-cart"
          >
            Commander – {selectedBundle ? selectedBundle.price.toFixed(2) : product.price?.toFixed(2)}€
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white border-t border-gray-200 p-3">
        <Button 
          size="lg"
          onClick={handleAddToCart}
          className="w-full bg-[#1a1a1a] hover:bg-black text-white font-jakarta font-semibold py-4 rounded-full"
          data-testid="mobile-add-to-cart-btn"
        >
          Acheter – {selectedBundle ? selectedBundle.price.toFixed(2) : product.price?.toFixed(2)}€
        </Button>
      </div>
      
      <div className="h-20 lg:hidden" />
    </div>
  );
};

export default ProductPage;
